import { useCallback, useEffect, useState } from "react";
import { CaretDown, UserCircle } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { EmptyState } from "@/components/shared";
import { usePresentation } from "@/context/PresentationContext";
import { fmtPeriod, NOT_ASSESSED } from "@/lib/period";
import { Ring, ScoreBar } from "@/components/frameworkBits";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const MonthlySummary = ({ base, elementName }) => {
  const { presenting } = usePresentation();
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState(null);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: res } = await api.get(`${base}/monthly-summary`,
        { params: period ? { period } : {} });
      setData(res);
      setPeriod((prev) => prev || res.period);
    } catch (e) { setData({ error: errMsg(e) }); }
  }, [base, period]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <p className="label-caps animate-pulse" data-testid="ms-loading">Loading summary</p>;
  if (data.error) return <EmptyState testid="ms-error" title="Summary unavailable" message={data.error} />;

  return (
    <section className="panel overflow-hidden" data-testid="lec-monthly">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-6 py-5">
        <div>
          <h1 className="font-display text-2xl text-navy">Month wise summary</h1>
          <p className="text-xs text-muted-foreground">
            {elementName} — score and every respondent's answers for the selected period.
          </p>
        </div>
        {data.periods.length > 0 && (
          <Select value={period || data.period || ""} onValueChange={setPeriod}>
            <SelectTrigger data-testid="ms-period" className="h-9 w-40 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              {data.periods.map((p) => <SelectItem key={p} value={p}>{fmtPeriod(p)}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="grid grid-cols-1 gap-6 p-6 lg:grid-cols-[1fr_1.4fr]">
        <div className="flex flex-col items-center gap-3">
          <Ring value={data.score10} tone="stroke-gold" testid="ms-ring">
            <span className="font-mono text-lg tabular-nums text-navy">{data.score10 ?? "—"}</span>
          </Ring>
          <p className="label-caps" data-testid="ms-score">Element score /10</p>
          <p className="text-xs text-muted-foreground">
            {fmtPeriod(data.period) || "No period yet"}
            {data.score10 == null && ` · ${NOT_ASSESSED}`}
          </p>
        </div>
        <div className="space-y-4">
          {data.sub_scores10.map((s) => (
            <div key={s.code} data-testid={`ms-metric-${s.code}`}>
              <div className="flex items-baseline justify-between">
                <span className="text-xs text-foreground/80">{s.name}</span>
                <span className="font-mono text-xs tabular-nums text-navy">{s.score10 ?? "—"}</span>
              </div>
              <ScoreBar value={s.score10} className="bg-gold" />
            </div>
          ))}
          {data.sub_scores10.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Submit this element's survey to populate the scored summary.
            </p>
          )}
        </div>
      </div>

      <div className="border-t border-border">
        <div className="flex items-center justify-between px-6 py-4">
          <p className="label-caps">Respondent responses</p>
          <p className="text-xs text-muted-foreground">
            {data.respondents.length} respondent(s) · {data.total_questions} questions in this element
          </p>
        </div>
        <div className="divide-y divide-border">
          {data.respondents.map((r) => (
            <div key={r.respondent_id} data-testid={`ms-respondent-${r.respondent_id}`}>
              <button onClick={() => setOpenId(openId === r.respondent_id ? null : r.respondent_id)}
                      data-testid={`ms-respondent-toggle-${r.respondent_id}`}
                      className="flex w-full items-center justify-between gap-4 px-6 py-4 text-left transition-colors hover:bg-sky/10">
                <span className="flex items-center gap-3">
                  <UserCircle size={22} className="text-deepblue" />
                  <span>
                    <span className="block text-sm font-medium text-navy">{r.name}</span>
                    <span className="block text-xs text-muted-foreground">{r.email}</span>
                  </span>
                </span>
                <span className="flex items-center gap-4">
                  <span className="text-right">
                    <span className="block font-mono text-lg tabular-nums text-navy">
                      {r.average10 ?? "—"}<span className="text-xs text-muted-foreground">/10</span>
                    </span>
                    <span className="label-caps">{r.answers.length} answered</span>
                  </span>
                  <CaretDown size={14}
                             className={`transition-transform ${openId === r.respondent_id ? "rotate-180" : ""}`} />
                </span>
              </button>
              {openId === r.respondent_id && (
                <div className="bg-muted/30 px-6 pb-5">
                  <div className="hidden grid-cols-[1fr_10rem_5rem] gap-3 py-2 text-[9px] uppercase tracking-[0.12em] text-muted-foreground md:grid">
                    <span>Question</span><span>Response</span><span>Score</span>
                  </div>
                  <div className="divide-y divide-border/70">
                    {r.answers.map((a) => (
                      <div key={a.question_code} data-testid={`ms-answer-${r.respondent_id}-${a.question_code}`}
                           className="grid grid-cols-1 gap-1 py-3 md:grid-cols-[1fr_10rem_5rem] md:items-start md:gap-3">
                        <span className="text-xs text-navy">
                          {!presenting && (
                            <span className="font-mono text-[10px] text-muted-foreground">{a.question_code} </span>
                          )}
                          {a.question_text}
                          {a.sub_element_name && (
                            <span className="mt-0.5 block text-[10px] uppercase tracking-[0.12em] text-muted-foreground">
                              {a.sub_element_name}
                            </span>
                          )}
                        </span>
                        <span className="text-xs text-foreground/80">
                          {a.na ? "Not applicable" : a.option_label || "—"}
                        </span>
                        <span className="font-mono text-xs tabular-nums text-navy">{a.score10 ?? "—"}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
          {data.respondents.length === 0 && (
            <EmptyState testid="ms-respondents-empty" title="No responses for this period"
                        message="Start the assessment for this element and answer its survey to see respondent-level responses here." />
          )}
        </div>
      </div>
    </section>
  );
};
